import { useMemo } from "react";
import { BookOpen, FileText, Folder, Lock } from "lucide-react";
import { useGlobalDocuments } from "@/hooks/useGlobalDocuments";
import { useCanManageAdminOnly } from "@/components/ManagersOnly";
import { DocsTreeNav } from "@/components/global-docs/DocsTreeNav";
import {
  type DocsTreeNode,
  MANAGEMENT_FOLDER_ID,
  buildDocsTree,
  firstDocument,
} from "@/components/global-docs/docsTree";
import { matchContextPath } from "../match";
import type { NavContextDef, NavItem } from "../types";

export const docPath = (docId: string) => `/docs/${docId}`;

function toItems(nodes: DocsTreeNode[]): NavItem[] {
  const items: NavItem[] = [];
  for (const node of nodes) {
    if (!node.is_folder) {
      items.push({
        key: node.id,
        label: node.title || "Untitled",
        href: docPath(node.id),
        icon: FileText,
      });
      continue;
    }
    // Folders have no page: they point at their first document and are
    // left out when they contain none.
    const first = firstDocument(node.children);
    if (!first) continue;
    items.push({
      key: node.id,
      label: node.title || "Untitled",
      href: docPath(first.id),
      icon: node.id === MANAGEMENT_FOLDER_ID ? Lock : Folder,
      children: toItems(node.children),
    });
  }
  return items;
}

/**
 * All documents, nested by folder, for the location trail and the section
 * pickers. The sidebar renders the full tree via `Body`.
 */
function useDocsItems(): NavItem[] {
  const { data: docs } = useGlobalDocuments();
  const canSeeManagement = useCanManageAdminOnly();
  return useMemo(
    () => toItems(buildDocsTree(docs, canSeeManagement)),
    [docs, canSeeManagement],
  );
}

export const docsContext: NavContextDef = {
  type: "docs",
  typeLabel: "Docs",
  icon: BookOpen,
  match: (pathname) =>
    matchContextPath("/docs/:docId", pathname) ??
    matchContextPath("/docs", pathname),
  key: () => "docs",
  base: () => "/docs",
  parent: () => ({ label: "Dashboard", href: "/" }),
  useHeader: () => ({ kicker: "Library", title: "Docs" }),
  useItems: useDocsItems,
  Body: DocsTreeNav,
};
